import { Component, ElementRef, viewChild, input, effect, HostListener, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { axisBottom, axisLeft, scaleBand, scaleLinear, select } from 'd3';

export interface HorizontalGroupedBarChartData {
  [key: string]: string | number;
}

@Component({
  selector: 'app-horizontal-grouped-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './horizontal-grouped-bar-chart.component.html',
  styleUrl: './horizontal-grouped-bar-chart.component.scss'
})
export class HorizontalGroupedBarChartComponent {
  readonly chartContainer = viewChild<ElementRef>('horizontalGroupedBarChart');

  public data = input.required<HorizontalGroupedBarChartData[]>();
  public margin = input({ top: 16, right: 48, bottom: 20, left: 150 });
  public chartHeight = input<number>(260);
  public barHeight = input<number>(12);
  public labelKey = input<string>('label');
  public subgroups = input<Array<{ name: string, legend: string, color: string }>>([]);
  public tooltipUnit = input<string>('');
  public showBackgroundBars = input<boolean>(false);
  public backgroundBarColor = input<string>('#F1F2F4');
  public barBorderRadius = input<number>(3);
  public xTicks = input<number[]>([]);
  public showPercentage = input<boolean>(false);
  public showLegend = input<boolean>(false);

  // Computed legend items for template binding
  legendItems = computed(() => {
    return this.subgroups().map(s => ({
      name: s.legend || s.name,
      color: s.color
    }));
  });

  constructor() {
    effect(() => {
      if (this.data()?.length && this.chartContainer()?.nativeElement && this.subgroups()?.length) {
        this.createChart();
      }
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.data()?.length && this.chartContainer()?.nativeElement && this.subgroups()?.length) {
      this.createChart();
    }
  }

  private createChart(): void {
    const container = this.chartContainer()?.nativeElement;
    const data = this.data();
    const subgroupConfig = this.subgroups();
    const labelKey = this.labelKey();

    if (!container || !data?.length || !subgroupConfig?.length) return;

    const boundingRect = container.getBoundingClientRect();
    const margin = this.margin();
    const fullHeight = this.chartHeight();

    // Clear previous chart
    select(container).selectAll('*').remove();

    const width = boundingRect.width - margin.left - margin.right;
    const height = fullHeight - margin.top - margin.bottom;
    
    const svg = select(container)
      .append('svg')
      .attr('width', boundingRect.width)
      .attr('height', fullHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top + -8})`);

    const subgroupNames = subgroupConfig.map(s => s.name);
    const colorMap = new Map(subgroupConfig.map(s => [s.name, s.color]));

    const groups = data.map(d => String(d[labelKey]));

    // Find max value across all subgroups
    let maxVal = 0;
    data.forEach(d => {
      subgroupNames.forEach(name => {
        const val = Number(d[name]) || 0;
        if (val > maxVal) maxVal = val;
      });
    });

    // X scale (values, horizontal)
    const xMax = maxVal > 0 ? Math.ceil(maxVal * 1.1) : 10;
    const xScale = scaleLinear().domain([0, xMax]).range([0, width]);

    // Y scale (categories, vertical)
    const y0 = scaleBand()
      .domain(groups)
      .range([0, height])
      .padding(0.3);

    // Inner scale for subgroups within each category
    const barH = this.barHeight();
    const groupHeight = barH * subgroupNames.length;
    const y1 = scaleBand()
      .domain(subgroupNames)
      .range([0, groupHeight])
      .padding(0.1);

    // X axis ticks
    const customXTicks = this.xTicks();
    const xTickValues = customXTicks.length > 0
      ? customXTicks
      : xScale.ticks(5);

    // Grid lines
    svg.append('g')
      .attr('class', 'grid')
      .call(
        axisBottom(xScale)
          .tickValues(xTickValues)
          .tickSize(height)
          .tickFormat(() => '')
      )
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick line')
          .attr('stroke', '#E6E6E6')
          .attr('stroke-width', 1);
      });

    // X axis labels at bottom
    const showPercentage = this.showPercentage();
    svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(
        axisBottom(xScale)
          .tickValues(xTickValues)
          .tickSize(0)
          .tickFormat(d => showPercentage ? `${d}%` : `${d}`)
      )
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('text')
          .attr('dy', '1.1em')
          .style('fill', '#9FA1A4')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .style('font-family', '"Nunito Sans", sans-serif');
      });

    // Y axis labels (category names, left-aligned with truncation)
    const labelPadding = 12;
    const maxLabelWidth = margin.left - labelPadding;
    const truncateText = (text: string, maxWidth: number): string => {
      const avgCharWidth = 7;
      const maxChars = Math.floor(maxWidth / avgCharWidth);
      if (text.length <= maxChars) return text;
      return text.substring(0, maxChars - 3) + '...';
    };

    svg.append('g')
      .attr('class', 'y-axis')
      .call(axisLeft(y0).tickSize(0))
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('text')
          .attr('x', -margin.left + labelPadding)
          .attr('text-anchor', 'start')
          .style('fill', '#9FA1A4')
          .style('font-size', '12px')
          .style('font-weight', '600')
          .style('font-family', '"Nunito Sans", sans-serif')
          .style('cursor', 'default')
          .each(function () {
            const textEl = select(this);
            const fullText = textEl.text();
            const truncated = truncateText(fullText, maxLabelWidth);
            if (truncated !== fullText) {
              textEl.text(truncated);
              textEl.style('cursor', 'pointer');
              textEl.append('title').text(fullText);
            }
          });
      });

    // Tooltip
    const tooltip = select(container)
      .append('div')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('border-radius', '8px')
      .style('padding', '4px 6px')
      .style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
      .style('border', '1px solid #e0e0e0')
      .style('pointer-events', 'none')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('z-index', '999');

    const barBorderRadius = this.barBorderRadius();
    const tooltipUnit = this.tooltipUnit();
    const component = this;

    // Background bars (optional)
    if (this.showBackgroundBars()) {
      const bgColor = this.backgroundBarColor();
      svg.append('g')
        .selectAll('g')
        .data(data)
        .enter()
        .append('g')
        .attr('transform', d => {
          const bandY = y0(String(d[labelKey]))!;
          const offset = (y0.bandwidth() - groupHeight) / 2;
          return `translate(0,${bandY + offset})`;
        })
        .selectAll('rect')
        .data(() => subgroupNames.map(name => ({ key: name })))
        .enter()
        .append('rect')
        .attr('x', 0)
        .attr('y', d => y1(d.key)!)
        .attr('width', width)
        .attr('height', y1.bandwidth())
        .attr('fill', bgColor)
        .attr('rx', barBorderRadius);
    }

    // Grouped bars
    svg.append('g')
      .selectAll('g')
      .data(data)
      .enter()
      .append('g')
      .attr('transform', d => {
        const bandY = y0(String(d[labelKey]))!;
        const offset = (y0.bandwidth() - groupHeight) / 2;
        return `translate(0,${bandY + offset})`;
      })
      .selectAll('rect')
      .data(d => subgroupNames.map(name => ({ key: name, value: Number(d[name]) || 0, label: String(d[labelKey]) })))
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', d => y1(d.key)!)
      .attr('width', d => xScale(d.value))
      .attr('height', y1.bandwidth())
      .attr('fill', d => colorMap.get(d.key)!)
      .attr('rx', barBorderRadius)
      .on('mouseover', function (event, d) {
        const current = data.find(item => String(item[labelKey]) === d.label);
        tooltip
          .style('opacity', 1)
          .html(`
            <div style="font-weight: 600; font-size: 13px; color: #2B2B2B; margin-bottom: 1px;">
              ${d.label}
            </div>
            ${subgroupNames.map(name => `
              <div style="display: flex; align-items: center; margin-bottom: 1px; font-family: 'Nunito Sans', sans-serif;">
                <div style="width: 12px; height: 12px; background: ${colorMap.get(name)}; border-radius: 50%; margin-right: 6px;"></div>
                <div style="color: #9FA1A4; font-size: 12px; flex: 1; margin-right: 8px;">${subgroupConfig.find(group => group.name === name)?.legend || name}</div>
                <div style="color: #9FA1A4; font-size: 12px;">${component.formatTooltipValue(Number(current?.[name]) || 0)}</div>
              </div>`).join('')}
          `);
      })
      .on('mousemove', function (event) {
        const tooltipEl = tooltip.node() as HTMLDivElement;
        const tooltipWidth = tooltipEl.offsetWidth;
        const containerRect = container.getBoundingClientRect();

        if (containerRect.right - event.clientX < tooltipWidth) {
          tooltip
            .style('left', event.offsetX - tooltipWidth - 5 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        } else {
          tooltip
            .style('left', event.offsetX + 10 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        }
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0);
      });
  }

  private formatTooltipValue(value: number): string {
    const formatted = value.toLocaleString();
    const unit = this.tooltipUnit();
    return unit ? `${formatted} ${unit}` : formatted;
  }
}